import React, { memo } from 'react'
import { useTranslation } from 'react-i18next'

import { TradeDetailsHistoryModal } from '@pages/Trade/Portfolio/Modals/TradeDetailsHistoryModal'

import { useModal } from '@context/ModalsContext'

import { Body2 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { InfoIcon } from '@ui/icons/Info.icon'
import { SkeletonRow } from '@ui/v2/Skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadSort,
  TableHeader,
  TableRow,
} from '@ui/v2/Table/Table'

import { CurrencyType } from '@model/WalletDto'

import { isClosePositionEffect, isOpenPositionEffect } from '../../utils/DxOrder'
import type { HistoryOrderWithLots } from '../hooks/useHistoryOrdersWithLots'
import { useOrderNetPnl } from '../hooks/useOrderNetPnl'
import type { OvernightFeesMap } from '../utils/DxtradeCashTransfersFields'
import {
  formatDate,
  formatSymbol,
  renderCellValue,
  renderPnl,
  renderSide,
} from '../utils/formatters'
import { EmptyPortfolioState } from './EmptyPortfolioState'

type RowProps = {
  order: HistoryOrderWithLots
  accountCurrency?: CurrencyType
  overnightFeesMap?: OvernightFeesMap
}

const HistoryOrderRow: React.FC<RowProps> = memo(({ order, accountCurrency, overnightFeesMap }) => {
  const { t } = useTranslation()
  const tradeDetailsHistoryModal = useModal(TradeDetailsHistoryModal)
  const positionEffect = order.legs?.[0]?.positionEffect
  const positionCode = order.legs?.[0]?.positionCode
  const positionEffectLabel = isOpenPositionEffect(positionEffect)
    ? t('Portfolio.Opened')
    : t('Portfolio.Closed')
  const overnightFees =
    isClosePositionEffect(positionEffect) && positionCode
      ? overnightFeesMap?.get(positionCode) ?? null
      : null
  const netPnl = useOrderNetPnl(order, overnightFees)

  return (
    <TableRow>
      <TableCell>{formatSymbol(order.instrument, order.description)}</TableCell>
      <TableCell>{renderSide(order.side, Body2)} </TableCell>
      <TableCell>{positionEffectLabel}</TableCell>
      <TableCell align='right' sensitive>
        {renderCellValue(order.sizeLots)}
      </TableCell>
      <TableCell align='right' sensitive={isClosePositionEffect(positionEffect)}>
        {isClosePositionEffect(positionEffect) ? renderPnl(netPnl, Body2, accountCurrency) : '-'}
      </TableCell>
      <TableCell align='right'>{formatDate(order.transactionTime ?? undefined)}</TableCell>
      <TableCell align='right'>
        <IconButton.Ghost
          aria-label={'Portfolio.Info'}
          onClick={() =>
            tradeDetailsHistoryModal.open({
              order,
              accountCurrency,
              overnightFees,
            })
          }
          icon={{ icon: InfoIcon, color: 'text-low-em' }}
        />
      </TableCell>
    </TableRow>
  )
})

type Props = {
  orders: HistoryOrderWithLots[]
  isLoading: boolean
  accountCurrency?: CurrencyType
  isLoadingMore?: boolean
  overnightFeesMap?: OvernightFeesMap
  hasActiveFilters?: boolean
}

export const HistoryOrdersTable: React.FC<Props> = memo(function HistoryOrdersTable({
  orders,
  isLoading,
  accountCurrency,
  isLoadingMore,
  overnightFeesMap,
  hasActiveFilters,
}: Props) {
  const { t } = useTranslation()

  const noResults = !orders?.length ? (
    <EmptyPortfolioState hasActiveFilters={hasActiveFilters} />
  ) : null

  return (
    <>
      <Table
        isLoading={isLoading}
        sticky='left'
        noResults={noResults}
        skeletonRows={5}
        skeletonColumns={7}
      >
        <TableHeader>
          <TableRow>
            <TableHead>{t('Portfolio.Asset')}</TableHead>
            <TableHeadSort>{t('Portfolio.Type')}</TableHeadSort>
            <TableHeadSort>{t('Portfolio.Position effect')}</TableHeadSort>
            <TableHeadSort align='right'>{t('Portfolio.Size')}</TableHeadSort>
            <TableHeadSort align='right'>{t('Portfolio.P&L')}</TableHeadSort>
            <TableHeadSort align='right'>{t('Portfolio.Date')}</TableHeadSort>
            <TableHeadSort align='right' />
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((order) => (
            <HistoryOrderRow
              key={order.orderId}
              order={order}
              accountCurrency={accountCurrency}
              overnightFeesMap={overnightFeesMap}
            />
          ))}
          {isLoadingMore ? (
            <>
              {Array.from({ length: 2 }).map((_, index) => (
                <SkeletonRow key={`history-skeleton-${index}`} columns={7} />
              ))}
            </>
          ) : null}
        </TableBody>
      </Table>
    </>
  )
})
